import { useMemo } from "react";
import { Box } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { useReportData } from "./UseReportData";
import { ChartCard, Empty, KpiBand, PlanActualChart, SectionFrame, SimpleTable, StatusDot, StatusKey, STATUS, bucketLabel, bucketTitle, fmt, fmtPct, signed, statusOf, useChartColors, varPct } from "./Reportkit";

const OZ = 31.1035;
const grade = (g, t) => (t ? g / t : null);

function Body({ d }) {
  const c = useChartColors();
  const t = d.totals;
  const ore = d.routes.filter((r) => r.cls === "Ore");
  const head = grade(t.auG, t.auT), headP = grade(t.planAuG, t.planAuT);
  const cuG = ore.reduce((s, r) => s + r.cuG, 0), cuT = ore.reduce((s, r) => s + r.cuT, 0), pCuG = ore.reduce((s, r) => s + r.planCuG, 0), pCuT = ore.reduce((s, r) => s + r.planCuT, 0);
  const cu = grade(cuG, cuT), cuP = grade(pCuG, pCuT);

  const trend = d.trend.map((x) => ({ label: bucketLabel(x.bucket, d.grain), title: bucketTitle(x.bucket, d.grain), actual: grade(x.auG, x.auT), plan: grade(x.planAuG, x.planAuT) }));
  const gold = d.trend.map((x) => ({ label: bucketLabel(x.bucket, d.grain), title: bucketTitle(x.bucket, d.grain), actual: x.auG / OZ, plan: x.planAuG / OZ }));

  const byFace = useMemo(() => {
    const m = new Map();
    ore.forEach((r) => {
      const o = m.get(r.loadLocation) || { name: r.loadLocation, mat: r.material, t: 0, g: 0, gt: 0, pg: 0, pgt: 0 };
      o.t += r.actual; o.g += r.auG; o.gt += r.auT; o.pg += r.planAuG; o.pgt += r.planAuT; m.set(r.loadLocation, o);
    });
    return [...m.values()].map((o) => ({ ...o, grade: grade(o.g, o.gt), plan: grade(o.pg, o.pgt) })).sort((a, b) => b.t - a.t);
  }, [ore]);

  const byMat = useMemo(() => {
    const m = new Map();
    ore.forEach((r) => {
      const o = m.get(r.material) || { material: r.material, t: 0, g: 0, gt: 0, pg: 0, pgt: 0, pt: 0 };
      o.t += r.actual; o.pt += r.plan; o.g += r.auG; o.gt += r.auT; o.pg += r.planAuG; o.pgt += r.planAuT; m.set(r.material, o);
    });
    return [...m.values()];
  }, [ore]);

  if (!ore.length) return <ChartCard title="Grades & Quality"><Empty>No ore in the current selection — grades apply to ore only (set Material to All or Ore).</Empty></ChartCard>;

  return (
    <>
      <KpiBand items={[
        { label: "Head grade Au", value: head == null ? "—" : `${fmt(head, 3)} g/t`, sub: `Plan ${fmt(headP, 3)}`, status: statusOf(head, headP), delta: varPct(head, headP) },
        { label: "Contained gold", value: `${fmt(t.auG / OZ)} oz`, sub: `Plan ${fmt(t.planAuG / OZ)} oz`, status: statusOf(t.auG, t.planAuG), delta: varPct(t.auG, t.planAuG) },
        { label: "Contained gold", value: `${fmt(t.auG / 1000, 1)} kg`, sub: "SUM(g/t × t)" },
        { label: "Copper", value: cu == null ? "—" : `${fmt(cu, 0)} ppm`, sub: `Plan ${fmt(cuP, 0)}` },
        { label: "Ore with grade", value: `${fmt(t.auT / 1000, 0)} kt`, sub: `of ${fmt(t.oreActual / 1000, 0)} kt ore` },
      ]} />
      <ChartCard title="Head grade — plan vs actual" sub={`By ${d.grain}, tonnage-weighted (never an average of averages)`}>
        <PlanActualChart data={trend} line valueFmt={(v) => `${fmt(v, 3)} g/t`} yFmt={(v) => fmt(v, 2)} domain={["auto", "auto"]} />
        <StatusKey />
      </ChartCard>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <ChartCard title="Contained gold (oz)" sub="Ore tonnes × g/t ÷ 31.1035"><PlanActualChart data={gold} valueFmt={(v) => `${fmt(v)} oz`} height={250} /></ChartCard>
        <ChartCard title="Grade by ore face" sub="Actual g/t, coloured vs plan grade">
          <ResponsiveContainer width="100%" height={Math.max(220, byFace.length * 40 + 40)}>
            <BarChart data={byFace} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={c.grid} />
              <XAxis type="number" tick={c.tick} tickFormatter={(v) => fmt(v, 2)} />
              <YAxis type="category" dataKey="name" width={96} tick={c.tick} />
              <RTooltip formatter={(v, n, p) => [`${fmt(v, 3)} g/t (plan ${fmt(p.payload.plan, 3)})`, "Actual"]} />
              <Bar dataKey="grade" barSize={16} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {byFace.map((x, i) => <Cell key={i} fill={STATUS[statusOf(x.grade, x.plan)].color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>
      <ChartCard title="Grade reconciliation" sub="Tonnes × grade = metal, by ore type">
        <SimpleTable cols={[
          { key: "material", label: "Ore type", align: "left" },
          { key: "t", label: "Tonnes", render: (r) => `${fmt(r.t)} t` },
          { key: "g", label: "Au g/t", render: (r) => fmt(grade(r.g, r.gt), 3) },
          { key: "pg", label: "Plan g/t", render: (r) => fmt(grade(r.pg, r.pgt), 3) },
          { key: "oz", label: "Au oz", render: (r) => fmt(r.g / OZ) },
          { key: "v", label: "Grade var", render: (r) => <>{signed(grade(r.g, r.gt) - grade(r.pg, r.pgt), 3, " g/t")} <StatusDot status={statusOf(grade(r.g, r.gt), grade(r.pg, r.pgt))} size={12} /></> },
        ]} rows={byMat} max={260} />
      </ChartCard>
      <span style={{ fontSize: 12, opacity: 0.7 }}>Grade targets exist only on plan rows that carry one (today HG ore), so plan grade covers fewer tonnes than actual. Moisture units are unconfirmed (DQ-16) and are not shown.</span>
    </>
  );
}

export default function GradesSection({ req }) {
  const st = useReportData("production", req);
  return (
    <SectionFrame eyebrow="Ore quality" title="Grades & Quality" desc="Gold head grade, contained gold and copper — tonnage-weighted, plan vs actual" state={st}>
      {st.data && <Body d={st.data} />}
    </SectionFrame>
  );
}